import React from "react";
import Sparkline from "./Sparkline";
import ChangeBadge from "./ChangeBadge";
import GoalProgressBadge from "./GoalProgressBadge";
import MetricInfoIcon from "../ui/MetricInfoIcon";
import type { MetricSample } from "../../hooks/useMetricSamples";
import { HEX } from "../../lib/colorSystem";

interface GoalData {
  goal: number;
  daysElapsed?: number;
  totalDays?: number;
}

interface MetricCardProps {
  label: string;
  value: string;
  /** Change vs previous period (absolute or percentage depending on changeFormat) */
  change?: number;
  /** How to display the change badge. Default "percent". */
  changeFormat?: "percent" | "dollar" | "number";
  sparkData?: number[];
  metricKey?: string;
  getSamples?: (key: string) => MetricSample[];
  /** Goal data for goal-based comparison (replaces change if provided) */
  goalData?: GoalData;
  /** Current numeric value (required if using goalData) */
  numericValue?: number;
  /** Hero sizing — T2 (38px) instead of T3 (24px) for the primary metric */
  hero?: boolean;
  /** Optional subtitle shown below the value */
  sub?: string;
  /** When present, the whole card becomes an interactive trigger (e.g. opens a breakdown modal) */
  onClick?: () => void;
}

export default function MetricCard({
  label,
  value,
  change,
  changeFormat = "percent",
  sparkData,
  metricKey,
  getSamples,
  goalData,
  numericValue,
  hero = false,
  sub,
  onClick,
}: MetricCardProps) {
  // Determine sparkline color based on goal progress or change
  const sparklineColor = goalData
    ? numericValue && goalData.goal > 0
      ? numericValue / goalData.goal >= 0.85
        ? HEX.green
        : HEX.red
      : HEX.accent
    : change !== undefined
      ? change > 0
        ? HEX.green
        : change < 0
          ? HEX.red
          : HEX.accent
      : HEX.accent;

  const interactiveProps = onClick
    ? {
        role: "button" as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};

  return (
    <div
      className={`liquid-glass anim-lift ${hero ? "px-6 py-5" : "px-5 py-3.5"} ${onClick ? "cursor-pointer" : ""}`}
      {...interactiveProps}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="token-10 text-glass-tertiary uppercase mb-1.5">
            {metricKey && getSamples ? (
              <MetricInfoIcon metricKey={metricKey} getSamples={getSamples}>
                {label}
              </MetricInfoIcon>
            ) : (
              label
            )}
          </div>
          <div className={`${hero ? "token-38" : "token-24"} text-glass metric-value`}>{value}</div>
          {sub && <div className="token-13m text-glass-muted mt-1">{sub}</div>}
        </div>
        {sparkData && sparkData.length > 0 && <Sparkline data={sparkData} color={sparklineColor} />}
      </div>
      {/* Goal-based progress badge (preferred) or change badge (legacy) */}
      {goalData && numericValue !== undefined ? (
        <div className="flex items-center gap-2 mt-2.5">
          <GoalProgressBadge
            current={numericValue}
            goal={goalData.goal}
            daysElapsed={goalData.daysElapsed}
            totalDays={goalData.totalDays}
          />
        </div>
      ) : change !== undefined ? (
        <div className="mt-2.5">
          <ChangeBadge value={change} format={changeFormat} />
        </div>
      ) : null}
    </div>
  );
}
